import { useState, type FormEvent } from 'react'
import { Link, Navigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthContext'

/**
 * Регистрация родителя по invite-токену.
 * URL: /parent-register?token=...
 *
 * После успеха backend выставляет teacher.role='PARENT' и создаёт parent_link.
 * Frontend сразу видит роль и редиректит в /parent.
 */
export function ParentRegisterPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { teacher, register } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (teacher) {
    return <Navigate to={teacher.role === 'PARENT' ? '/parent' : '/app'} replace />
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-soft to-white p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
          <div className="text-5xl mb-3">🔗</div>
          <h1 className="font-semibold text-gray-900 mb-2">{t('parentRegisterPage.needTokenTitle')}</h1>
          <p className="text-sm text-gray-600">
            {t('parentRegisterPage.needTokenText')}{' '}
            <Link to="/login" className="text-primary hover:underline">{t('parentRegisterPage.loginLinkLower')}</Link>.
          </p>
        </div>
      </div>
    )
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (!email.trim() || password.length < 8 || !displayName.trim()) return
    setError(null)
    setLoading(true)
    try {
      await register(email.trim(), password, displayName.trim(), token)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-soft to-white p-4">
      <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md">
        <div className="flex items-center gap-3 mb-6">
          <img src="/edpad-logo.png" alt="EdPad" className="w-10 h-10 rounded-lg" />
          <div>
            <h1 className="text-xl font-semibold">{t('parentRegisterPage.heading')}</h1>
            <p className="text-sm text-gray-500">{t('parentRegisterPage.subheading')}</p>
          </div>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-900 mb-5">
          {t('parentRegisterPage.inviteInfo')}
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">{t('parentRegisterPage.fullNameLabel')}</div>
            <input
              type="text"
              className="input-field"
              placeholder={t('parentRegisterPage.fullNamePlaceholder')}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              required
            />
          </label>
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">{t('parentRegisterPage.emailLabel')}</div>
            <input
              type="email"
              className="input-field"
              placeholder="parent@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </label>
          <label className="block">
            <div className="text-sm font-medium text-gray-700 mb-1">{t('parentRegisterPage.passwordLabel')}</div>
            <input
              type="password"
              className="input-field"
              placeholder={t('parentRegisterPage.passwordPlaceholder')}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={8}
              autoComplete="new-password"
            />
          </label>

          {error && <div className="text-sm text-red-600">{error}</div>}

          <button
            type="submit"
            disabled={loading || !email.trim() || password.length < 8 || !displayName.trim()}
            className="btn-primary w-full"
          >
            {loading ? t('parentRegisterPage.submitting') : t('parentRegisterPage.submit')}
          </button>

          <p className="text-xs text-gray-500 text-center mt-2">
            {t('parentRegisterPage.haveAccount')}{' '}
            <Link to="/login" className="text-primary hover:underline">{t('parentRegisterPage.loginLink')}</Link>.{' '}
            {t('parentRegisterPage.agreePrefix')}{' '}
            <Link to="/privacy" className="text-primary hover:underline">{t('parentRegisterPage.privacyLink')}</Link>.
          </p>
        </form>
      </div>
    </div>
  )
}
